/**
 * Types for the Complaint <-> Logistics linkage (PRD: Complaint ↔ Logistics linkage,
 * Service Material Movement).
 *
 * A solo logistics ticket raised under one of the complaint-linked project categories carries the
 * complaint it was raised for — see `complaintLinkedCategories.ts` for which those are. These
 * types cover both directions of that link: the complaint picker on the logistics creation form,
 * and the linked-ticket tracking view inside the complaint drawer.
 */

/**
 * Normalised view of one complaint, used by the picker options and the read-only summary card.
 *
 * This is a front-end view model, not an API shape: it is mapped from a complaints list row by
 * `utils/complaintLogisticsPicker.ts` when picking on the logistics form, and from the drawer's
 * complaint detail payload when raising from a complaint. Keeping both paths behind one type is
 * what lets the summary card render identically either way.
 */
export type IComplaintSummary = {
    complaint_id: number
    display_id: string
    status: string
    status_code?: string
    complaint_type?: string | null
    complaint_sub_type?: string | null
    site_name?: string | null
    charger_id?: string | null
    /** When the complaint was raised. Kept under the list API's own name rather than renamed. */
    created_at?: string | null
    owner_name?: string | null
    owner_department?: string | null
    /**
     * Used to pre-fill the destination POC when raising from the complaint drawer. The complaint
     * does not reliably carry a site contact, so both may be null — in that case the POC fields
     * are left blank rather than pre-filled with a placeholder.
     */
    site_poc_name?: string | null
    site_poc_contact?: string | null
}

/** One material line of a linked logistics ticket, as shown by the in-place "View items" expander. */
export type ILinkedLogisticsItem = {
    sku_code: string
    item_name?: string | null
    serial_number?: string | null
    requested_qty?: number | string | null
    uom?: string | null
}

export type ILinkedLogisticsTicket = {
    ticket_id: number
    display_id: string
    status: string
    status_code?: string
    created_at?: string | null
    updated_at?: string | null
    /** Requested date of delivery — mandatory at creation, so always present. */
    requested_delivery_date?: string | null
    /**
     * The planned delivery date, distinct from the requested one: it is set and revised as the
     * dispatch progresses, so it is the closer answer to "when does this arrive".
     */
    tentative_delivery_date?: string | null
    /** Both arrive from the dispatch-details flow; null until dispatch is initiated. */
    job_work_id?: string | null
    dc_number?: string | null
    is_cancelled?: boolean
    /**
     * Present when this ticket is a split-delivery clone. Names the *immediate* parent, so a
     * chained split chips its direct parent rather than the original ticket.
     */
    split_parent_display_id?: string | null
    items?: ILinkedLogisticsItem[]
    /**
     * Total line-item count on the ticket. `items` is capped server-side, so when this exceeds
     * the returned length the card links out to Logistics instead of growing the drawer.
     */
    items_total_count?: number
}

/** Which surface created the complaint link. Reporting only. */
export enum EComplaintLinkSource {
    LOGISTICS_FORM = "LOGISTICS_FORM",
    COMPLAINT_DRAWER = "COMPLAINT_DRAWER",
    SPLIT_CLONE = "SPLIT_CLONE",
}
